import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServicemodulesTravelexpensesServiceModuleIdEntriesCompanySummaries } from '@/api/generated/company-management-user/company-management-user'
import type { TravelExpenseSummaryModel } from '@/api/model'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { TableCard } from '@/components/common/table/TableCard'
import { businessCost, carCost, formatEuro, formatOdometer } from '../travelExpenseFormat'
import { unwrap } from '@/api/unwrap'

interface Props {
  serviceModuleId: number
  year: number
  month: number
  onRowClick: (employeeUserId: string) => void
}

const COL_COUNT = 11

/** Monthly overview: one row per employee with their sums, plus a total row. */
export function TravelExpenseSummaryTable({ serviceModuleId, year, month, onRowClick }: Props) {
  const { t } = useTranslation(['service-modules-travel-expenses', 'common'])
  const query = useGetApiCompaniesCurrentServicemodulesTravelexpensesServiceModuleIdEntriesCompanySummaries(
    serviceModuleId,
    { year, month },
  )
  const rows = unwrap<TravelExpenseSummaryModel[]>(query.data) ?? []

  const sum = (pick: (row: TravelExpenseSummaryModel) => number) => rows.reduce((acc, row) => acc + pick(row), 0)

  return (
    <TableCard>
      <div className="flex-1 overflow-auto scrollbar-custom relative">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
            <TableRow>
              <TableHead>{t('columns.employee', { ns: 'common' })}</TableHead>
              <TableHead className="text-right">{t('summary.days')}</TableHead>
              <TableHead className="text-right">{t('table.dailyAllowance')}</TableHead>
              <TableHead className="text-right">{t('table.kilometers')}</TableHead>
              <TableHead className="text-right">{t('table.overnightCost')}</TableHead>
              <TableHead className="text-right">{t('table.hospitalityCost')}</TableHead>
              <TableHead className="text-right">{t('table.businessCost')}</TableHead>
              <TableHead className="text-right">{t('table.carCost')}</TableHead>
              <TableHead className="text-right">{t('table.odometer')}</TableHead>
              <TableHead className="text-right">{t('table.receipts')}</TableHead>
              <TableHead className="text-right">{t('table.total')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoaderTable
              isLoading={query.isLoading}
              isError={query.isError}
              isEmpty={rows.length === 0}
              colSpan={COL_COUNT}
              errorMessage={t('summary.loadError')}
              emptyMessage={t('summary.empty')}
            >
              {rows.map((row) => (
                <TableRow
                  key={row.employeeUserId}
                  className="cursor-pointer"
                  onClick={() => row.employeeUserId && onRowClick(row.employeeUserId)}
                >
                  <TableCell>{`${row.employeeFirstName ?? ''} ${row.employeeLastName ?? ''}`.trim() || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.entryCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(row.dailyAllowance)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.kilometers}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(row.overnightCost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(row.hospitalityCost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(businessCost(row))}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(carCost(row))}</TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {formatOdometer(row.startKilometers, row.endKilometers)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.receiptCount}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatEuro(row.total)}</TableCell>
                </TableRow>
              ))}
            </LoaderTable>
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow className="font-medium">
                <TableCell>{t('summary.total')}</TableCell>
                <TableCell className="text-right tabular-nums">{sum((r) => r.entryCount)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum((r) => r.dailyAllowance))}</TableCell>
                <TableCell className="text-right tabular-nums">{sum((r) => r.kilometers)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum((r) => r.overnightCost))}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum((r) => r.hospitalityCost))}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum(businessCost))}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum(carCost))}</TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">{sum((r) => r.receiptCount)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatEuro(sum((r) => r.total))}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </TableCard>
  )
}
